import { useMemo } from 'react';
import { CAR_FLOW_TYPE } from '~/data/car/carCardData';
import { ROUTES } from '~/data/routes';
import { PAYMENT_METHOD } from '~/data/searchFlowData';
import { CarCardType } from '~/data/types/car/cars';
import {
  USED_CAR_PAYMENT_METHOD,
  UsedCarType,
} from '~/data/types/usedCar/usedCarData';
import { useAppSelector } from '~/redux-toolkit/store/hooks';
import { RootState } from '~/redux-toolkit/store/store';
import RESOURCES from '~/resources';
import { useCarDealOptions } from '~/hooks/useCarDealOptions';
import useGetCarTypeParam from '~/hooks/useGetCarTypeParam';
import { useRouter } from 'next/router';

export const useInitialCarDealOption = (
  car: CarCardType | UsedCarType | null,
  carType?: string,
) => {
  const router = useRouter();

  const carOptions = useCarDealOptions(car);
  const carTypeFromHook = useGetCarTypeParam();
  const carTypeParam = carType ?? carTypeFromHook;

  const { priceRange, paymentMethod } = useAppSelector(
    (state: RootState) => state?.resultPage?.query?.filters?.budget,
  );
  const { usedCarQuery } = useAppSelector(
    (state: RootState) => state?.usedCarFilters,
  );

  const isMonthlyPayment = useMemo(
    () =>
      Boolean(paymentMethod === PAYMENT_METHOD.monthly && priceRange.minPrice),
    [paymentMethod, priceRange],
  );

  const isMonthlyUsedCarPayment = useMemo(
    () =>
      Boolean(
        usedCarQuery.filters.budget.paymentMethod ===
          USED_CAR_PAYMENT_METHOD.monthly,
      ),
    [usedCarQuery.filters.budget.paymentMethod],
  );

  const isOneTimePayment = useMemo(
    () =>
      Boolean(paymentMethod === PAYMENT_METHOD.oneTime && priceRange.minPrice),
    [paymentMethod, priceRange],
  );

  const isLeasingCar = useMemo(() => {
    const currentURL = decodeURI(router.asPath);
    return (
      currentURL.includes(RESOURCES.NAV_PARAM_PRIVATE_LEASING_CAR) ||
      (carOptions.includes(CAR_FLOW_TYPE.LEASING_CAR) &&
        !carOptions.includes(CAR_FLOW_TYPE.LOAN_CAR))
    );
  }, [router.asPath, carOptions]);

  return useMemo(() => {
    if (isLeasingCar) {
      return CAR_FLOW_TYPE.LEASING_CAR;
    }

    if (carTypeParam === ROUTES.USED_CAR) {
      return isMonthlyUsedCarPayment &&
        (car as UsedCarType).smartPrice?.webPriceLoanMinMonthlyPayment
        ? CAR_FLOW_TYPE.LOAN_CAR
        : CAR_FLOW_TYPE.NEW_CAR;
    }
    if (isMonthlyPayment && carOptions.includes(CAR_FLOW_TYPE.LOAN_CAR)) {
      // If is monthly payment selected then default is CAR_FLOW_TYPE.LOAN_CAR
      return CAR_FLOW_TYPE.LOAN_CAR;
    }

    // If is one time payment selected then default is CAR_FLOW_TYPE.NEW_CAR
    if (isOneTimePayment && carOptions.includes(CAR_FLOW_TYPE.NEW_CAR)) {
      return CAR_FLOW_TYPE.NEW_CAR;
    }

    // if not montjly payment and not onetime payment - default is CAR_FLOW_TYPE.LOAN_CAR if exist
    if (carOptions.includes(CAR_FLOW_TYPE.LOAN_CAR)) {
      return CAR_FLOW_TYPE.LOAN_CAR;
    }
    if (carOptions.includes(CAR_FLOW_TYPE.ONLINE_PRICE)) {
      return CAR_FLOW_TYPE.ONLINE_PRICE;
    }

    // otherwise return the first option to be default

    return carOptions[0];
  }, [
    car,
    carOptions,
    carTypeParam,
    isMonthlyPayment,
    isMonthlyUsedCarPayment,
    isOneTimePayment,
    isLeasingCar,
  ]);
};
